/**
 * Public blog listing page — published posts from AI Blog admin pipeline.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, Loader2 } from 'lucide-react';
import { getPublicBlogPosts } from '../services/apiClient';
import { setSEO } from '../utils/seo';
import { DEFAULT_BLOG_IMAGE } from '../utils/blogImages';
import BlogCover from '../components/BlogCover';

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSEO({
      title: 'Blog — Tolbert Tactical',
      description: 'Tactical gear tips, Utah community news, and exclusive giveaway updates from Tolbert Tactical.',
      path: '/blog',
      image: DEFAULT_BLOG_IMAGE,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicBlogPosts();
        if (!cancelled) setPosts(res?.posts || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load blog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Page header */}
        <header className="mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="glass-card-dark p-2.5 rounded-xl">
              <BookOpen className="h-6 w-6 text-theme-bronze" />
            </div>
            <p className="text-sm font-medium tracking-wide uppercase text-theme-bronze/80">
              From the field
            </p>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-theme-silver mb-3">
            Blog
          </h1>
          <p className="text-lg text-theme-silver-light max-w-2xl">
            Tactical insights, gear guides, and community updates from Tolbert Tactical.
          </p>
          <div className="mt-6 h-px w-24 bg-gradient-to-r from-theme-bronze to-transparent" />
        </header>

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-theme-bronze" />
            <p className="text-sm text-theme-silver/60">Loading articles…</p>
          </div>
        )}

        {error && (
          <div className="glass-card-dark p-8 text-center border border-red-500/20">
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-sm text-theme-silver/60">Please try again in a moment.</p>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="glass-card-dark p-12 text-center">
            <BookOpen className="h-10 w-10 text-theme-bronze/50 mx-auto mb-4" />
            <p className="text-theme-silver text-lg mb-2">No articles published yet</p>
            <p className="text-theme-silver/60 text-sm mb-6">
              Check back soon for gear tips and giveaway updates.
            </p>
            <Link to="/giveaways" className="btn-primary inline-flex items-center gap-2">
              Browse giveaways <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {!loading && !error && featured && (
          <div className="space-y-6">
            {/* Featured / latest */}
            <Link
              to={`/blog/${featured.slug}`}
              className="block glass-card-dark overflow-hidden hover:shadow-glow transition-all duration-300 group border border-theme-bronze/20"
            >
              <BlogCover
                post={featured}
                alt=""
                className="aspect-[21/9] sm:aspect-[2.4/1] min-h-[10rem]"
                imgClassName="transition-transform duration-500 group-hover:scale-[1.03]"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-theme-black/80 via-theme-black/20 to-transparent pointer-events-none" />
                <div className="absolute top-4 left-4 z-10 flex flex-wrap items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md bg-theme-bronze/90 text-theme-black">
                    Latest
                  </span>
                </div>
              </BlogCover>
              <div className="p-6 md:p-8">
                {featured.publishedAt && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-theme-silver/50 mb-3">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(featured.publishedAt)}
                  </span>
                )}
                <h2 className="text-2xl md:text-3xl font-bold text-theme-silver group-hover:text-theme-bronze transition-colors mb-3">
                  {featured.title}
                </h2>
                {featured.excerpt && (
                  <p className="text-theme-silver-light text-base md:text-lg leading-relaxed line-clamp-3 mb-5">
                    {featured.excerpt}
                  </p>
                )}
                {featured.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {featured.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-1 rounded-md bg-glass-white/10 text-theme-silver/70"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <span className="inline-flex items-center gap-2 text-theme-bronze font-medium text-sm group-hover:gap-3 transition-all">
                  Read article <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>

            {/* Remaining posts */}
            {rest.length > 0 && (
              <ul className="grid gap-4 md:gap-5">
                {rest.map((post) => (
                  <li key={post.id || post.slug}>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="block glass-card-dark overflow-hidden hover:shadow-glow hover:border-theme-bronze/30 transition-all duration-300 group border border-transparent"
                    >
                      <div className="flex flex-col sm:flex-row">
                        <BlogCover
                          post={post}
                          alt=""
                          className="sm:w-44 md:w-52 shrink-0 aspect-[16/10] sm:aspect-auto sm:min-h-[8.5rem]"
                          imgClassName="transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                        <div className="p-5 md:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 flex-1 min-w-0">
                          <div className="min-w-0 flex-1">
                            <h2 className="text-lg md:text-xl font-semibold text-theme-silver group-hover:text-theme-bronze transition-colors mb-2">
                              {post.title}
                            </h2>
                            {post.excerpt && (
                              <p className="text-theme-silver/70 text-sm leading-relaxed line-clamp-2 mb-3">
                                {post.excerpt}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3">
                              {post.publishedAt && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-theme-silver/45">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {formatDate(post.publishedAt)}
                                </span>
                              )}
                              {post.tags?.slice(0, 2).map((tag) => (
                                <span
                                  key={tag}
                                  className="text-xs px-2 py-0.5 rounded bg-glass-white/10 text-theme-silver/60"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <span className="shrink-0 inline-flex items-center gap-1.5 text-theme-bronze text-sm font-medium self-start sm:mt-1">
                            Read <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
